import React, { useMemo, useState } from 'react';
import { Modal, Select, Button, Space, Typography } from 'antd';
import { UseGetItUsers } from '../../hooks/genericHooks';
import type { ItUsers } from '../../types';

export interface UserSelectProps {
  open: boolean;
  value?: string[]; // preselected usernames
  onOk: (usernames: string[]) => void;
  onCancel: () => void;
  title?: string;
  description?: string;
}

const UserSelect: React.FC<UserSelectProps> = ({ open, value, onOk, onCancel, title, description }) => {
  const { data: src = [], isLoading } = UseGetItUsers() as any;
  const users: ItUsers[] = useMemo(() => (Array.isArray(src) ? src : (src?.data ?? [])), [src]);

  const [selected, setSelected] = useState<string[]>(value || []);

  const options = useMemo(() => {
    return (users || [])
      .map((u) => {
        const label = u.username || `user_${u.id}`;
        return { value: String(u.username), label };
      })
      .filter(Boolean);
  }, [users]);

  return (
    <Modal
      open={open}
      title={title || 'Select Users'}
      onCancel={() => {
        onCancel();
        // do not reset selection on cancel so parent can reopen with same defaults if desired
      }}
      destroyOnClose
      maskClosable
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="ok"
          type="primary"
          onClick={() => onOk(Array.from(new Set(selected)))}
          disabled={selected.length === 0}
        >
          Select
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {description ? (
          <Typography.Text type="secondary">{description}</Typography.Text>
        ) : null}
        <Select
          mode="multiple"
          loading={isLoading}
          showSearch
          value={selected}
          onChange={setSelected}
          style={{ width: '100%' }}
          placeholder="Select one or more users"
          options={options}
          filterOption={(input, option) =>
            ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())
          }
          optionFilterProp="label"
        />
      </Space>
    </Modal>
  );
};

export default UserSelect;
